/**
 * ATTEST-UI-1 screen 4 — the ET 1.200 independence gate.
 *
 * A real step, not a checkbox buried in a form. The firm attests; the engine
 * fails closed. Two things worth knowing while filling this in, both stated on
 * screen because getting them wrong is a professional problem rather than a
 * form-validation problem:
 *
 *  - Declaring an interest in the subject entity blocks the letter whatever the
 *    interest type. An unrecognised type is treated as impairing.
 *  - Attesting "no impairing interest" while an interest in the subject is
 *    declared is a contradiction, and the engine surfaces it as a worse finding
 *    than the disclosed conflict alone. The UI therefore never quietly clears
 *    the attestation when an interest is added — the CPA resolves it, not us.
 *
 * Signing identity is shown, not typed (ATTEST-EVIDENCE-INTEGRITY-1a): the firm
 * legal name, CPA name and licence number come from the active firm profile at
 * generation time. What this screen still collects is what only the engagement's
 * CPA can answer — peer review and the ET 1.200 attestations below.
 */
import type { AttestPolicy, LetterTypeSummary } from '@/components/attest/api'
import type { IssuerFormState } from '@/components/attest/formState'
import { INDEPENDENCE_FIELD_HELP } from '@/components/attest/independenceHelp'
import {
  previewEligibility,
  previewIndependence,
} from '@/components/attest/independencePreflight'
import { IndependenceVerdict } from '@/components/attest/IndependenceVerdict'
import {
  engagementRequiresAttestEligibleIssuer,
  independenceRequiredFields,
  RequiredFieldsNote,
  RequiredMark,
} from '@/components/attest/requiredFields'
import { SigningIdentityCard } from '@/components/attest/SigningIdentityCard'
import { Button } from '@/components/ui/button'
import { HelpDot } from '@/components/ui/help-dot'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** `_IMPAIRING_INTEREST_TYPES` in `core/attest/independence.py`. */
const INTEREST_TYPES = [
  'equity', 'loan', 'officer', 'director',
  'trustee', 'family', 'contingent_fee', 'joint_venture',
]

function Check({
  id, checked, onChange, help, required = false, children,
}: {
  id: string
  checked: boolean
  onChange: (v: boolean) => void
  help: keyof typeof INDEPENDENCE_FIELD_HELP
  /** Starred? Decided by `requiredFields`, never by this component. */
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2">
      <label className="flex flex-1 items-start gap-3 text-sm" htmlFor={id}>
        <input
          id={id}
          data-testid={id}
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
        />
        <span>
          {children}
          {required && <RequiredMark field={id} />}
        </span>
      </label>
      <HelpDot {...INDEPENDENCE_FIELD_HELP[help]} testId={`info-${help}`} />
    </div>
  )
}

export interface IndependenceStepProps {
  letterType: LetterTypeSummary
  issuer: IssuerFormState
  onChange: (patch: Partial<IssuerFormState>) => void
  /**
   * The entity the letter is about. Only used for the live preview below — the
   * gate that counts still runs on the server at Build.
   */
  subjectEntity?: string
  /**
   * Only to tell whether this engagement type demands an attest-eligible
   * issuer, which decides whether the licence and peer-review boxes are
   * required (ATTEST-UX-1 D1). Null before the policy loads, in which case
   * neither is starred — see `needsAttestEligibleIssuer`.
   */
  policy?: AttestPolicy | null
}

export function IndependenceStep({
  letterType, issuer, onChange, subjectEntity = '', policy = null,
}: IndependenceStepProps) {
  const required = independenceRequiredFields(letterType, issuer, policy)
  // ATTEST-INDEPENDENCE-MATRIX-1 (G.1): show the verdict while the CPA fills
  // the form in, not five steps later. Advisory — see independencePreflight.ts.
  const independence = previewIndependence(issuer, subjectEntity)
  // PREFLIGHT-BASIS-AWARE-1: same derivation the Step-4 stars use (below), so
  // the "would this block?" preview can never disagree with what got starred.
  const eligibility = previewEligibility(
    issuer,
    engagementRequiresAttestEligibleIssuer(letterType, policy),
  )
  const blocked = independence.evaluated
    ? !independence.independent || !eligibility.eligible
    : !eligibility.eligible
  const setInterest = (index: number, patch: Partial<IssuerFormState['declaredInterests'][0]>) => {
    onChange({
      declaredInterests: issuer.declaredInterests.map(
        (it, i) => (i === index ? { ...it, ...patch } : it),
      ),
    })
  }

  return (
    <div className="space-y-6">
      <RequiredFieldsNote />

      {letterType.independence_required && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-semibold">{letterType.display_name}</span> requires an
          independent issuer. The letter cannot be finalised while independence is impaired.
        </div>
      )}

      <IndependenceVerdict
        blocked={blocked}
        independence={independence}
        eligibility={eligibility}
      />

      <SigningIdentityCard help={<HelpDot {...INDEPENDENCE_FIELD_HELP.signingIdentity} testId="info-signingIdentity" />} />

      <section className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="peer-review-expiry">
            Peer review expires
            <HelpDot {...INDEPENDENCE_FIELD_HELP.peerReviewExpiry} testId="info-peerReviewExpiry" />
          </Label>
          <Input
            id="peer-review-expiry"
            data-testid="peer-review-expiry"
            type="date"
            className="mt-1"
            value={issuer.peerReviewExpiry}
            onChange={e => onChange({ peerReviewExpiry: e.target.value })}
          />
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-border p-4">
        <h3 className="text-sm font-semibold">The firm attests that</h3>
        <Check
          id="attested-no-interest"
          checked={issuer.attestedNoImpairingInterest}
          onChange={v => onChange({ attestedNoImpairingInterest: v })}
          help="attestedNoImpairingInterest"
          // Either/or: the star lifts once an interest is declared below,
          // because attesting *and* declaring is the contradiction the engine
          // treats as worse than the disclosed conflict alone.
          required={required.has('attested-no-interest')}
        >
          It holds no interest that impairs independence with respect to the subject
          entity (AICPA Code of Professional Conduct ET 1.200).
        </Check>
        <Check
          id="licensed-for-attest"
          checked={issuer.licensedForAttest}
          onChange={v => onChange({ licensedForAttest: v })}
          help="licensedForAttest"
          required={required.has('licensed-for-attest')}
        >
          It is licensed to perform attest work in the engagement state.
        </Check>
        <Check
          id="peer-review-current"
          checked={issuer.peerReviewCurrent}
          onChange={v => onChange({ peerReviewCurrent: v })}
          help="peerReviewCurrent"
          required={required.has('peer-review-current')}
        >
          Its peer review is current.
        </Check>
        <Check
          id="platform-back-office"
          checked={issuer.isPlatformBackOffice}
          onChange={v => onChange({ isPlatformBackOffice: v })}
          help="isPlatformBackOffice"
        >
          This firm is the platform&rsquo;s own back-office firm. (It may never issue an
          attest letter — leave unticked for an external issuing firm.)
        </Check>
      </section>

      <section className="rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              Declared interests
              <HelpDot {...INDEPENDENCE_FIELD_HELP.declaredInterests} testId="info-declaredInterests" />
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Any interest in the subject entity blocks the letter, whatever its type.
              Declare it anyway — an undisclosed interest is the worse outcome.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="add-interest"
            onClick={() => onChange({
              declaredInterests: [
                ...issuer.declaredInterests,
                { subject_entity: '', interest_type: 'equity', description: '' },
              ],
            })}
          >
            Declare an interest
          </Button>
        </div>

        {issuer.declaredInterests.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">None declared.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {issuer.declaredInterests.map((interest, i) => (
              <div key={i} className="grid gap-2 md:grid-cols-[1fr_auto_1fr_auto]">
                <Input
                  aria-label="Entity"
                  data-testid={`interest-entity-${i}`}
                  placeholder="Entity"
                  value={interest.subject_entity}
                  onChange={e => setInterest(i, { subject_entity: e.target.value })}
                />
                <select
                  aria-label="Interest type"
                  data-testid={`interest-type-${i}`}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={interest.interest_type}
                  onChange={e => setInterest(i, { interest_type: e.target.value })}
                >
                  {INTEREST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <Input
                  aria-label="Description"
                  placeholder="Description"
                  value={interest.description}
                  onChange={e => setInterest(i, { description: e.target.value })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`remove-interest-${i}`}
                  onClick={() => onChange({
                    declaredInterests: issuer.declaredInterests.filter((_, j) => j !== i),
                  })}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
